import type { ComponentType, PropsWithChildren, ReactNode } from "react";

type ProviderEntry = {
  Component: ComponentType<PropsWithChildren<any>>;
  props: Record<string, unknown>;
};

/**
 * Builds a type-checked provider entry for `composeProviders`. The component's
 * props (minus `children`, which composition supplies) are inferred and
 * enforced at the call site, so a wrong/missing prop fails to compile here.
 * The `props` argument is optional only when the component has no required
 * non-children props.
 */
export const provider = <P extends PropsWithChildren>(
  Component: ComponentType<P>,
  ...[props]: {} extends Omit<P, "children">
    ? [props?: Omit<P, "children">]
    : [props: Omit<P, "children">]
): ProviderEntry => ({
  Component: Component as ComponentType<PropsWithChildren<any>>,
  props: (props ?? {}) as Record<string, unknown>,
});

/**
 * Nests an ordered list of providers around `children`. The first entry is the
 * outermost provider, so array order matches nesting order. Per-entry props are
 * validated by `provider`; the spread here is the single unchecked boundary.
 */
export const composeProviders = (
  entries: ProviderEntry[],
  children: ReactNode,
): ReactNode =>
  entries.reduceRight(
    (acc, { Component, props }) => <Component {...props}>{acc}</Component>,
    children,
  );
